/** The localStorage key the PWA writes today's rotating API host to (same-origin handoff). */
export const API_DOMAIN_STORAGE_KEY = 'vn-pwa:api-domain'

/**
 * Resolve the global storage safely. A locked-down / storage-disabled WebView throws SecurityError
 * on the very `localStorage` property access, so we cannot let that access sit in a default-param
 * expression unguarded — it would blow up before any try/catch in the body could run.
 */
const resolveStorage = (): Storage | undefined => {
  try {
    return localStorage
  } catch {
    return undefined
  }
}

/** Read the bare API host the PWA discovered, or null if discovery has not run this origin yet. */
export const readApiDomain = (storage: Pick<Storage, 'getItem'> | undefined = resolveStorage()): string | null => {
  try {
    return storage?.getItem(API_DOMAIN_STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

/** The two backend URLs the dashboard talks to, derived from the runtime host. */
export interface RuntimeEndpoints {
  restEndpointUrl: string
  graphqlEndpoint: string
}

/**
 * Build the REST + GraphQL URLs from a bare domain, exactly as the PWA's authorizedFetch does
 * (`https://<domain>...`), so both clients target the identical origin the beacon verified.
 */
export const resolveEndpoints = (domain: string): RuntimeEndpoints => ({
  restEndpointUrl: `https://${domain}`,
  graphqlEndpoint: `https://${domain}/graphql`,
})

/**
 * Send the user back to the PWA root. Used on any auth failure (401) or when the API host is
 * missing/unreachable: the PWA owns discovery, so it re-verifies a live domain, re-persists it,
 * and — the session still valid — returns to `/app`.
 */
export const bounceToPwa = (location: Pick<Location, 'assign'> = window.location): void => {
  location.assign('/')
}
